/**
 * Analytics generique — Google Analytics 4 + Consent Mode v2 + Microsoft Clarity (optionnel).
 *
 * Regles strictes :
 *  - Aucun appel reseau (gtag.js, google-analytics.com, clarity.ms) tant que
 *    l'utilisateur n'a pas explicitement accorde la mesure d'audience.
 *  - Aucune donnee personnelle (email, telephone, nom) dans les parametres.
 *  - Aucun evenement en doublon (dedup au niveau des helpers audio / FAQ /
 *    section_view / scroll_depth).
 *
 * Debug local : ajouter `?ga_debug=1` a l'URL, ou etre sur localhost, pour
 * voir chaque evenement dans la console.
 */
import { GA_MEASUREMENT_ID, CLARITY_PROJECT_ID } from "@/config/analytics";

// ---------- Types ----------

type ConsentValue = "granted" | "denied";

type ConsentState = {
  ad_storage?: ConsentValue;
  ad_user_data?: ConsentValue;
  ad_personalization?: ConsentValue;
  analytics_storage?: ConsentValue;
  functionality_storage?: ConsentValue;
  security_storage?: ConsentValue;
  wait_for_update?: number;
};

type GtagConfigParams = Record<string, string | number | boolean>;

export type GtagEventParams = Record<
  string,
  string | number | boolean | undefined
>;

type GtagArgs =
  | ["js", Date]
  | ["consent", "default" | "update", ConsentState]
  | ["config", string, GtagConfigParams?]
  | ["event", string, GtagEventParams?]
  | ["set", GtagConfigParams];

type GtagFn = (...args: GtagArgs) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
    clarity?: (...args: unknown[]) => void;
  }
}

export type ConsentCategories = {
  analytics: boolean;
  marketing: boolean;
};

// ---------- Etat interne ----------

let initialized = false;
let gtagScriptLoaded = false;
let clarityLoaded = false;
let analyticsGranted = false;

function isDebug(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local") ||
    host.endsWith(".lovable.app");
  const flag = new URLSearchParams(window.location.search).get("ga_debug") === "1";
  return flag || isLocal;
}

function debugLog(name: string, params?: GtagEventParams): void {
  if (!isDebug()) return;
  // eslint-disable-next-line no-console
  console.info("[analytics]", name, params ?? {});
}

/**
 * IMPORTANT : gtag.js n'interprete QUE les entrees `dataLayer` qui sont des
 * objets `arguments` (pas des tableaux). Un `dataLayer.push([...])` brut est
 * ignore silencieusement : c'est pourquoi aucun hit ne partait vers
 * google-analytics.com. On passe donc systematiquement par la vraie fonction
 * `gtag()` qui fait `dataLayer.push(arguments)`.
 */
function ensureGtagFn(): GtagFn | undefined {
  if (typeof window === "undefined") return undefined;
  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag !== "function") {
    // eslint-disable-next-line prefer-rest-params
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    }
    window.gtag = gtag as unknown as GtagFn;
  }
  return window.gtag;
}

function pushGtag(...args: GtagArgs): void {
  const gtag = ensureGtagFn();
  if (!gtag) return;
  (gtag as (...a: unknown[]) => void)(...args);
}

function ensureGtagScript(): void {
  if (typeof window === "undefined" || gtagScriptLoaded) return;
  gtagScriptLoaded = true;
  // Config AVANT l'injection du script (ordre dataLayer garanti).
  pushGtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    url_passthrough: true,
    ads_data_redaction: true,
    debug_mode: true,
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  document.head.appendChild(s);
}


// ---------- Init ----------

/**
 * Pose UNIQUEMENT dataLayer + Consent Mode v2 par defaut ("denied").
 * Ne charge PAS gtag.js — le script n'est injecte qu'apres consentement
 * explicite de la mesure d'audience.
 */
export function initAnalytics(): void {
  if (typeof window === "undefined" || initialized) return;
  initialized = true;

  ensureGtagFn();

  pushGtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  pushGtag("js", new Date());

  bindContactClickTracking();

  debugLog("init", { consent: "default_denied", script_loaded: false });
}

// ---------- Consent ----------

export function grantConsent(categories: ConsentCategories): void {
  if (typeof window === "undefined") return;
  analyticsGranted = categories.analytics;
  pushGtag("consent", "update", {
    analytics_storage: categories.analytics ? "granted" : "denied",
    ad_storage: categories.marketing ? "granted" : "denied",
    ad_user_data: categories.marketing ? "granted" : "denied",
    ad_personalization: categories.marketing ? "granted" : "denied",
  });
  debugLog("consent_update", {
    analytics: categories.analytics,
    marketing: categories.marketing,
  });
  if (categories.analytics) {
    ensureGtagScript();
    flushQueuedEvents();
  }
}

export function denyConsent(): void {
  if (typeof window === "undefined") return;
  analyticsGranted = false;
  pushGtag("consent", "update", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  debugLog("consent_denied", {});
}

export function isAnalyticsGranted(): boolean {
  return analyticsGranted;
}

// ---------- Persistance du choix utilisateur ----------

const CONSENT_KEY = "djval_consent_v2";

export function readStoredConsent(): ConsentCategories | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentCategories>;
    return {
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
    };
  } catch {
    return null;
  }
}

export function storeConsent(categories: ConsentCategories): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(categories));
  } catch {
    /* stockage indisponible : le choix vaut pour la session */
  }
}

// ---------- Exclusion du back-office ----------

/**
 * Le back-office (`/admin*`) est un usage interne : ses pages vues et ses
 * evenements ne doivent jamais polluer les statistiques du site public.
 */
export function isExcludedPath(path?: string): boolean {
  const p =
    path ??
    (typeof window !== "undefined" ? window.location.pathname : "");
  return p.startsWith("/admin");
}

// ---------- Hits primitifs ----------

/**
 * File d'attente : tout événement (page_view comprise) émis AVANT le
 * consentement est conservé puis rejoué au moment du `granted`. Sans cela,
 * avec `send_page_view:false`, la toute première page vue d'une visite
 * était définitivement perdue (chargement différé de gtag.js).
 */
const queuedEvents: Array<[string, GtagEventParams]> = [];

export function flushQueuedEvents(): void {
  if (!analyticsGranted) return;
  while (queuedEvents.length) {
    const next = queuedEvents.shift()!;
    // page_view : page_location / page_title sont recalculés au moment du
    // rejeu (le titre SSR initial peut avoir changé entre-temps).
    if (next[0] === "page_view") {
      const path = typeof next[1].page_path === "string" ? next[1].page_path : "";
      next[1].page_location =
        typeof window !== "undefined" ? window.location.origin + path : path;
      next[1].page_title =
        typeof document !== "undefined" ? document.title : undefined;
    }
    pushGtag("event", next[0], next[1]);
    debugLog(next[0], next[1]);
  }
}

export function trackPageView(path: string, title?: string): void {
  if (isExcludedPath(path)) return;
  if (!analyticsGranted) {
    // Consentement pas encore accordé : on met en file, le page_view partira
    // dès le `granted` (cf. flushQueuedEvents appelé dans grantConsent).
    if (queuedEvents.length < 50) queuedEvents.push(["page_view", { page_path: path }]);
    return;
  }
  const params: GtagEventParams = {
    page_path: path,
    page_location:
      typeof window !== "undefined" ? window.location.origin + path : path,
    page_title:
      title ?? (typeof document !== "undefined" ? document.title : undefined),
  };
  pushGtag("event", "page_view", params);
  debugLog("page_view", params);
}

export function trackEvent(name: string, params: GtagEventParams = {}): void {
  if (isExcludedPath()) return;
  const withPath: GtagEventParams = {
    page_path:
      typeof window !== "undefined"
        ? window.location.pathname + window.location.search
        : undefined,
    ...params,
  };
  if (!analyticsGranted) {
    if (queuedEvents.length < 50) queuedEvents.push([name, withPath]);
    return;
  }
  flushQueuedEvents();
  pushGtag("event", name, withPath);
  debugLog(name, withPath);
}

// ---------- Helpers metier (dedup + normalisation) ----------

const audioPlayed = new Set<string>();
const audioCompleted = new Set<string>();

export function trackAudioPlay(trackName: string): void {
  if (audioPlayed.has(trackName)) return;
  audioPlayed.add(trackName);
  trackEvent("audio_play", { track_name: trackName });
}

export function trackAudioComplete(trackName: string): void {
  if (audioCompleted.has(trackName)) return;
  audioCompleted.add(trackName);
  trackEvent("audio_complete", { track_name: trackName });
}

export function trackFaqOpen(question: string): void {
  trackEvent("faq_open", { faq_question: question.slice(0, 100) });
}

export function trackOutbound(url: string, ctaLocation: string): void {
  trackEvent("outbound_click", { link_url: url, cta_location: ctaLocation });
}

export function trackGalleryOpen(sectionName: string): void {
  trackEvent("gallery_open", { section_name: sectionName });
}

// ---------- Conversions ----------

/** Conversion : formulaire de devis envoye avec succes. */
export function trackDevisEnvoye(formName: string, prestation?: string): void {
  trackEvent("devis_submit", {
    form_name: formName,
    prestation: prestation || "non_precise",
    value: 1,
  });
}

/** Conversion : affichage de la page de remerciement /merci. */
export function trackDevisMerci(): void {
  trackEvent("devis_merci", { value: 1 });
}

/** Conversion : clic sur un lien telephone. */
export function trackClicTelephone(location: string): void {
  trackEvent("contact_phone", { cta_location: location, value: 1 });
}

/** Conversion : clic sur un lien email. */
export function trackClicEmail(location: string): void {
  trackEvent("contact_email", { cta_location: location, value: 1 });
}

/**
 * Capture globale des clics sur les liens `tel:` et `mailto:` (en-tete,
 * footer, hero, pages ville, chatbot...) sans instrumenter chaque lien.
 */
function bindContactClickTracking(): void {
  if (typeof document === "undefined") return;
  document.addEventListener(
    "click",
    (e) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest?.(
        'a[href^="tel:"], a[href^="mailto:"]',
      ) as HTMLAnchorElement | null;
      if (!link) return;
      const section = link.closest("header")
        ? "header"
        : link.closest("footer")
          ? "footer"
          : (link.getAttribute("data-cta-location") ??
            (typeof window !== "undefined" ? window.location.pathname : "page"));
      if (link.getAttribute("href")?.startsWith("mailto:")) trackClicEmail(section);
      else trackClicTelephone(section);
    },
    { capture: true },
  );
}

// ---------- Microsoft Clarity (optionnel) ----------

export function loadClarity(): void {
  if (typeof window === "undefined") return;
  if (clarityLoaded) return;
  if (!CLARITY_PROJECT_ID) return;
  if (!analyticsGranted) return;
  clarityLoaded = true;
  const id = CLARITY_PROJECT_ID;

  // Chargement défensif : snippet officiel Clarity (file `clarity.q` créée par
  // la fonction elle-même, insertion avant le premier <script> comme attendu
  // par le tag), le tout dans un try/catch.
  const start = () => {
    try {
      const w = window as unknown as Record<string, unknown>;
      if (typeof w["clarity"] !== "function") {
        const shim = function (...args: unknown[]) {
          const q = (shim.q = shim.q || []);
          q.push(args);
        } as ((...a: unknown[]) => void) & { q?: unknown[] };
        w["clarity"] = shim;
      }

      const t = document.createElement("script");
      t.async = true;
      t.src = "https://www.clarity.ms/tag/" + id;
      t.onerror = () => {
        /* échec réseau ou bloqueur de pub : sans conséquence */
      };
      const first = document.getElementsByTagName("script")[0];
      if (first && first.parentNode) first.parentNode.insertBefore(t, first);
      else document.head.appendChild(t);
      debugLog("clarity_loaded", { project_id: id });
    } catch {
      /* Clarity ne doit jamais casser la page */
    }
  };


  if (document.readyState === "complete") {
    window.setTimeout(start, 0);
  } else {
    window.addEventListener("load", () => window.setTimeout(start, 0), { once: true });
  }
}

